import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import {
  colorShadesGenerator,
  formatPaymentDictionary,
  generatePaymentOptionsDictionary,
  getNewUrlWithAllQueryParams,
  getStorage,
  isEmpty,
  updateStorage,
} from "../Utils/utils";
import { isNetworkLevelError, redirectToCanonicalCheckout } from "../Utils/redirectFallback";

const baseUrl = process.env.REACT_APP_BASE_URL;

const fetchSession = (token, browserTimeZone) =>
  axiosInstance.get(`${baseUrl}/checkout/sessions/${token}?browserTimeZone=${browserTimeZone}`);

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const paymentSession = createAsyncThunk(
  "paymentSessionDetails/paymentSession",
  async ({ token, browserTimeZone = '' }, { rejectWithValue }) => {
    const testScenario = localStorage.getItem('BOXPAY_TEST_SCENARIO');

    try {
      // ── First attempt ────────────────────────────────────────────────────
      if (testScenario === "retry_fail_first") {
        console.warn("[BoxPayTest] retry_fail_first → forcing attempt #1 to fail");
        throw { isMock: true, response: null, message: "Simulated first attempt failure" };
      }
      if (testScenario === "retry_fail_both") {
        console.warn("[BoxPayTest] retry_fail_both → forcing attempt #1 to fail");
        throw { isMock: true, response: null, message: "Simulated failure attempt #1" };
      }
      if (testScenario === "retry_404") {
        console.warn("[BoxPayTest] retry_404 → forcing attempt #1 to return 404");
        throw { isMock: true, response: { status: 404 }, message: "Simulated 404" };
      }

      const response = await fetchSession(token, browserTimeZone);
      return response?.data;
    } catch (firstError) {
      // 404 means the session genuinely does not exist — no point retrying
      if (firstError?.response?.status === 404) {
        throw new Error("Session not found");
      }

      // ── Wait 2 seconds then retry once ───────────────────────────────────
      await delay(2000);

      try {
        if (testScenario === "retry_fail_both") {
          console.warn("[BoxPayTest] retry_fail_both → forcing attempt #2 to fail");
          throw { isMock: true, response: null, message: "Simulated failure attempt #2" };
        }

        const retryResponse = await fetchSession(token, browserTimeZone);
        return retryResponse?.data;
      } catch (retryError) {
        if (retryError?.response?.status === 404) {
          throw new Error("Session not found");
        }
        if (isNetworkLevelError(retryError) && redirectToCanonicalCheckout()) {
          return rejectWithValue({ reasonCode: null, message: retryError?.message, redirected: true });
        }
        return rejectWithValue({
          reasonCode: retryError?.response?.data?.reasonCode || null,
          message: retryError?.message,
        });
      }
    }
  }
);
export const getRetryPolicyRemaining = createAsyncThunk(
  "paymentSessionDetails/getRetryPolicyRemaining",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/checkout/sessions/${token}/retry-policy/remaining`);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || null);
    }
  }
);

export const getAllPaymentMethods = createAsyncThunk(
  "paymentSessionDetails/getAllPaymentMethods",
  async ({ token, countryCode, amount, offerId, browserTimeZone }) => {
    try {
      let url = `${baseUrl}/checkout/sessions/${token}/payment-methods`;
      // Properly construct query parameters
      const params = new URLSearchParams();
      if (countryCode) params.append("customerCountryCode", countryCode);
      if (typeof amount === 'number') params.append("amount", amount);
      if (offerId) params.append("offerId", offerId);
      if (!countryCode && browserTimeZone) params.append("browserTimeZone", browserTimeZone)

      // Append query string if any parameters exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axiosInstance.get(url);
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);


const initialState = {
  sessionDetails: getStorage("session", "s_d") || {
    paymentDetails: {
      shopper: {},
    },
  },
  tokenType: "checkout-token",
  sessionDetailsLoaded: false,
  paymentMethodDictionary: getStorage("session", "paymentMethods") || undefined,
  paymentMethodDictionaryAll: getStorage("session", "paymentMethodsAll") || undefined,
  sessionDetailsLoading: false,
  errorMessage: "",
  darkColor: "#fff",
  lightColor: "#fff",
  selectionColor: "#fff",
  baseColor: "#fff",
  headerTextColor: "#fff",
  addressCollapse: getStorage("session", "addressCollapse") || false,
  latestSavedAddress: getStorage("session", "latestSavedAddress") || {},
  // Values entered in the "Additional Information" card: [{ fieldName, fieldValue }]
  additionalInfoValues: getStorage("session", "additionalInfoValues") || [],
  additionalInfoCollapsed: getStorage("session", "additionalInfoCollapsed") || false,
  isSessionExpired: false,
  isSessionInvalid: false,
  isSessionPaid: false,
  isShippingAddressDisplay: true,
  callerType: "",
  technicalError: false,
  sessionErrorReasonCode: null,
  updatedShopperDetails: {},
  sessionDetailsStatusReason: "",
  paymentMethodsLoading: false,
  paymentMethodsBasedOnCountry: [],
  paymentMethodsError: [],
  browserCountry: {},
  uniqueReference: "",
  sampleToken: false,
  showCancelBtn: false,
  uniqueCardNetworkArray: [],
  retryPolicyRemaining: [],
};
const extractStatusReason = (statusReason) => {
  if (statusReason && statusReason.includes(":")) {
    return statusReason.split(":").slice(1).join(":").trim();
  }
  return statusReason;
};

export const paymentSessionDetailsSlice = createSlice({
  name: "paymentSessionDetails",
  initialState,
  reducers: {
    updateUniqueReference: (state, action) => {
      state.uniqueReference = action.payload;
    },
    updateShopperDetails: (state, action) => {
      const {
        name = "",
        email,
        address1,
        address2,
        city,
        state: shopperState,
        countryCode,
        postalCode,
        gender,
        phoneNumber,
        labelName,
        panNumber,
        dateOfBirth,
        labelType,
        deliveryAddress,
      } = action.payload.selectedCardData;

      const parts = name.trim().split(" ");
      const lname = parts.length > 1 ? parts.pop() : "";
      const fname = parts.join(" ");

      const newShopper = {
        firstName: isEmpty(fname) ? undefined : fname,
        lastName: isEmpty(lname) ? undefined : lname,
        email,
        gender,
        phoneNumber,
        panNumber: isEmpty(panNumber) ? undefined : panNumber,
        dateOfBirth: isEmpty(dateOfBirth) ? undefined : dateOfBirth,
        customFields: state.additionalInfoValues?.length ? state.additionalInfoValues : undefined,
        deliveryAddress: {
          address1,
          address2,
          city,
          state: shopperState,
          countryCode,
          postalCode,
          labelName,
          labelType,
        },
        uniqueReference: state.sessionDetails?.paymentDetails?.shopper?.uniqueReference ? state.sessionDetails?.paymentDetails?.shopper?.uniqueReference : state.uniqueReference,
        // uniqueReference: state.uniqueReference ? state.uniqueReference : state.sessionDetails?.paymentDetails?.shopper?.uniqueReference,
        //TODO add unique reference on the basis of shopper login logic
      };
      state.updatedShopperDetails = newShopper;
    },

    updatePaymentMethods: (state, action) => {
      const newSessionDetails = {
        ...state.sessionDetails,
        sessionDetails: {
          ...state.sessionDetails,
          configs: {
            ...state.sessionDetails.configs,
            paymentMethods: action.payload,
          },
        },
      };

      return {
        ...state,
        sessionDetails: newSessionDetails,
      };
    },

    updateSavedAddressData: (state, action) => {
      state.latestSavedAddress = action.payload;
      sessionStorage.setItem("latestSavedAddress", JSON.stringify(action.payload));
    },

    removeUpdatedShopperDetails: (state, action) => {
      state.updatedShopperDetails = {};
      state.additionalInfoValues = [];
      state.additionalInfoCollapsed = false;
      updateStorage("session", "remove", "additionalInfoValues");
      updateStorage("session", "remove", "additionalInfoCollapsed");
    },
    removeAddressfield: (state) => {
      const updatedAddress = { ...state.latestSavedAddress };
      for (const key in updatedAddress) {
        if (key !== "labelType" && key !== "labelName") {
          delete updatedAddress[key];
        }
      }
      state.latestSavedAddress = updatedAddress;
      sessionStorage.setItem("latestSavedAddress", JSON.stringify(updatedAddress));
    },
    saveAdditionalInfo: (state, action) => {
      const customFields = action.payload?.length ? action.payload : [];
      state.additionalInfoValues = customFields;
      state.additionalInfoCollapsed = true;
      updateStorage("session", "update", "additionalInfoValues", customFields.length ? customFields : null);
      updateStorage("session", "update", "additionalInfoCollapsed", true);
      if (Object.keys(state.updatedShopperDetails || {}).length) {
        state.updatedShopperDetails = {
          ...state.updatedShopperDetails,
          customFields: customFields.length ? customFields : undefined,
        };
      }
    },
    openAdditionalInfo: (state) => {
      state.additionalInfoCollapsed = false;
      updateStorage("session", "remove", "additionalInfoCollapsed");
    },
    openAddressComponent: (state) => {
      state.addressCollapse = false;
    },
    collpaseAddressComponent: (state) => {
      updateStorage("session", "update", "addressCollapse", true);
      state.addressCollapse = true;
    },
    setTokenType: (state, action) => {
      state.tokenType = action.payload;
    },
    setIsSessionExpired: (state, action) => {
      state.isSessionExpired = action.payload;
    },

    setIsSessionInvalid: (state, action) => {
      state.isSessionInvalid = action.payload;
    },
    setIsSessionPaid: (state, action) => {
      state.isSessionPaid = action.payload;
    },
    updatePaymentMethodDictionary: (state, action) => {
      // need to confirm here if we need to replace the entire dictionary or merge it with the existing one
      let newDict = action.payload || {};
      state.paymentMethodDictionary = state.paymentMethodDictionary || {};
      newDict = Object.assign({}, state.paymentMethodDictionary, newDict);
      updateStorage("session", "update", "paymentMethods", newDict);
      state.paymentMethodDictionary = newDict;

      // let dict = generatePaymentOptionsDictionary(action.payload || {});
      // const originalMethods = dict
      // dict = formatPaymentDictionary(dict)
      // state.paymentMethodDictionary = dict;
      // state.paymentMethodDictionaryAll = originalMethods;

      // updateStorage("session", "update", "paymentMethods", dict);
      // updateStorage("session", "update", "paymentMethodsAll", originalMethods);
      // state.paymentMethodsLoading = false;
    },
    removePaymentMethods: (state) => {
      state.paymentMethodDictionary = undefined;
      sessionStorage.removeItem("paymentMethods");
    },
    // Rebuild the dictionary from the session's embedded payment methods (the same
    // source used on initial load). Used when clearing an offer: the /payment-methods
    // endpoint used by getAllPaymentMethods does NOT return BuyNowPayLater for some
    // sessions (backend inconsistency - the session embed includes it but that endpoint
    // omits it regardless of amount), so refetching there silently drops BNPL. Restoring
    // from configs.paymentMethods gives the correct full, un-filtered method list back.
    restorePaymentMethodsFromSession: (state) => {
      const methods = state.sessionDetails?.configs?.paymentMethods;
      if (!methods) return;
      const originalMethods = generatePaymentOptionsDictionary(methods);
      const dict = formatPaymentDictionary(originalMethods);
      state.paymentMethodDictionary = dict;
      state.paymentMethodDictionaryAll = originalMethods;
      // Refresh paymentMethodsBasedOnCountry too (getAllPaymentMethods, which this
      // replaces, used to set it). Use a fresh array reference so the effect in
      // deliveryAddress.jsx keyed on it re-runs and re-adds the Recommended (saved
      // instruments) tab for logged-in users - otherwise it would vanish on removal.
      state.paymentMethodsBasedOnCountry = [...methods];
      updateStorage("session", "update", "paymentMethods", dict);
      updateStorage("session", "update", "paymentMethodsAll", originalMethods);
    },
    storeSessionDetails: (state) => {
      updateStorage("session", "update", "s_d", state);
    },
    handleIsShippingAddressDisplay: (state, action) => {
      state.isShippingAddressDisplay = action.payload;
    },
    resetErrorMsg: (state, action) => {
      state.sessionDetailsStatusReason = "";
    },
    updateBrowserCountry: (state, action) => {
      state.browserCountry = action.payload;
    },
    setShowCancelBtn: (state, action) => {
      state.showCancelBtn = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(paymentSession.pending, (state) => {
        state.sessionDetailsLoading = true;
        state.sessionDetails = {}
      })
      .addCase(paymentSession.fulfilled, (state, action) => {
        updateStorage("session", "update", "s_d", action.payload);
        const uniqueReference = action.payload?.paymentDetails?.shopper?.uniqueReference;
        if (uniqueReference) {
          state.uniqueReference = uniqueReference;
        }
        state.sessionDetailsLoading = false;
        state.sessionDetails = action.payload;

        if (action.payload?.status === "PAID" && action.payload?.callerType) {
          const url = action.payload?.callerType === "CHECKOUT" ? `${window.location.origin}/finalStatus${location.search}` : `${window.location.origin}/finalStatus`;
          const newUrl = getNewUrlWithAllQueryParams(url, null, "token");
          if (!location.pathname.includes('/finalStatus') && newUrl) {
            window.location.replace(newUrl);
          }
        } else if (action.payload?.paymentAttemptsExhausted === true && !location.pathname.includes("/paymentFailed")) {
          // All order / checkout payment attempts are used up for this token.
          window.location.replace(`${window.location.origin}/paymentFailed`);
        }

        state.baseColor = action.payload?.merchantDetails?.checkoutTheme?.headerColor;
        state.headerTextColor = action.payload?.merchantDetails?.checkoutTheme?.headerTextColor;

        const shades = colorShadesGenerator(
          action.payload?.merchantDetails?.checkoutTheme?.headerColor
        );

        state.isSessionExpired = action.payload.status === "EXPIRED";

        state.sessionDetailsStatusReason = extractStatusReason(
          action.payload?.lastTransactionDetails?.status?.reason
        );

        state.sessionDetailsLoaded = true;

        if (action.payload?.configs) {
          let dict = generatePaymentOptionsDictionary(action.payload.configs.paymentMethods);

          const originalMethods = dict;
          let allPaymentMethods = action.payload?.configs?.paymentMethods || [];
          const paymentMethods = allPaymentMethods ?? [];
          const allowedTypes = new Set(["card", "wallet", "buynowpaylater"]);
          const uniqueLogos = new Map();
          paymentMethods.forEach((method) => {
            const type = method?.type?.toLowerCase();
            const logoUrl = method?.logoUrl;
            if (!allowedTypes.has(type) || !logoUrl) return
            if (!uniqueLogos.has(logoUrl)) {
              uniqueLogos.set(logoUrl, {
                logoUrl,
                title: method?.title || method?.brand || method?.type || "Payment method",
              });
            }
          });
          const poweredByLogos = Array.from(uniqueLogos.values());
          state.uniqueCardNetworkArray = poweredByLogos


          dict = formatPaymentDictionary(dict);
          state.paymentMethodDictionary = dict;
          state.paymentMethodDictionaryAll = originalMethods;

          updateStorage("session", "update", "uniqueCardNetworkArray", poweredByLogos);
          updateStorage("session", "update", "paymentMethods", dict);
          updateStorage("session", "update", "paymentMethodsAll", originalMethods);
          if (!action.payload?.paymentDetails?.onDemandAmount) {
            state.paymentMethodDictionary = dict;
            updateStorage("session", "update", "paymentMethods", dict);
          }
        }

        if (typeof shades[12] !== "undefined") {
          state.darkColor = "#" + shades[12].hex;
        }
        if (typeof shades[10] !== "undefined") {
          state.lightColor = "#" + shades[10].hex;
        }
        if (typeof shades[1] !== "undefined") {
          state.selectionColor = "#" + shades[1].hex;
        }

        if (action.payload.callerType) {
          state.callerType = action.payload.callerType;
        }

        if (action.payload.token.includes("st_")) {
          state.sampleToken = true;
        }
      })
      .addCase(paymentSession.rejected, (state, action) => {
        if (action.payload?.redirected) {
          state.sessionDetailsLoading = true;
          return;
        }

        state.sessionDetailsLoading = false;
        state.sessionDetails = [];
        state.sessionDetailsStatusReason = "";
        state.sessionErrorReasonCode = action.payload?.reasonCode || null;

        if (action.error?.message === "Session not found") {
          state.isSessionInvalid = true;
          state.technicalError = false;
          state.errorMessage = action.error.message;
        } else {
          state.errorMessage = action.payload?.message || action.error.message;
          state.isSessionInvalid = false;
          state.technicalError = true;
        }
      })
      .addCase(getRetryPolicyRemaining.fulfilled, (state, action) => {
        state.retryPolicyRemaining = action.payload || [];
      })
      .addCase(getRetryPolicyRemaining.rejected, (state) => {
        state.retryPolicyRemaining = [];
      })
      .addCase(getAllPaymentMethods.pending, (state) => {
        state.paymentMethodsLoading = true;
      })
      .addCase(getAllPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsBasedOnCountry = action.payload;
        let dict = generatePaymentOptionsDictionary(action.payload);
        const originalMethods = dict
        dict = formatPaymentDictionary(dict)
        state.paymentMethodDictionary = dict;
        state.paymentMethodDictionaryAll = originalMethods;

        updateStorage("session", "update", "paymentMethods", dict);
        updateStorage("session", "update", "paymentMethodsAll", originalMethods);
        state.paymentMethodsLoading = false;
      })
      .addCase(getAllPaymentMethods.rejected, (state, action) => {
        state.paymentMethodDictionary = {};
        state.paymentMethodDictionaryAll = {};
        state.paymentMethodsBasedOnCountry = [];
        state.paymentMethodsLoading = false;
        state.paymentMethodsError = action.error.payload;
      });
  }

});

export const selectCurrencyCode = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.paymentDetails?.money
    ?.currencyCode;

export const selectCurrencySymbol = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.paymentDetails?.money
    ?.currencySymbol;

export const selectToatalAmount = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.paymentDetails?.money?.amount;

export const selectPaymentDetails = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.paymentDetails;

export const selectLightColor = (state) =>
  state?.paymentSessionDetails?.lightColor;

export const selectDarkColor = (state) =>
  state?.paymentSessionDetails?.darkColor;

export const selectBaseColor = (state) =>
  state?.paymentSessionDetails?.baseColor;

export const selectHeaderTextColor = (state) => {
  let stateHeaderTxtColor = state?.paymentSessionDetails?.headerTextColor;
  let sessionHeaderTxtColor = getStorage('session', 's_d')?.merchantDetails?.checkoutTheme?.headerTextColor
  return sessionHeaderTxtColor || stateHeaderTxtColor;
}

export const selectSelectionColor = (state) =>
  state?.paymentSessionDetails?.selectionColor;

export const selectFontFamily = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.merchantDetails?.checkoutTheme?.font;

export const selectTemplate = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.merchantDetails?.template;

export const SelectMerchantDetails = (state) =>
  state?.paymentSessionDetails?.sessionDetails?.merchantDetails;

export const selectExpiryTimeStamp = (state) => {
  return state?.paymentSessionDetails?.sessionDetails?.sessionExpiryTimestamp;
};
export const selectFrontendBackUrl = (state) => {
  return state?.paymentSessionDetails?.sessionDetails?.paymentDetails?.frontendBackUrl;
};
export const selectFrontReturnUrl = (state) => {
  return state?.paymentSessionDetails?.sessionDetails?.paymentDetails?.frontendReturnUrl;
};
export const selectToken = (state) => {
  return state?.paymentSessionDetails?.sessionDetails?.token;
};
// Payment options must wait for the Additional Information card when the backend
// marked any of its (enabled) custom fields as mandatory.
export const selectIsAdditionalInfoPending = (state) => !!state?.additionalComponents?.hasMandatoryCustomFields && !state?.paymentSessionDetails?.additionalInfoCollapsed;
export const selectRetryPolicyRemaining = (state) => {
  return state?.paymentSessionDetails?.retryPolicyRemaining;
};

export const {
  updateUniqueReference,
  updateShopperDetails,
  updatePaymentMethods,
  updateSavedAddressData,
  removeAddressfield,
  removeUpdatedShopperDetails,
  openAddressComponent,
  saveAdditionalInfo,
  openAdditionalInfo,
  collpaseAddressComponent,
  setIsSessionExpired,
  setIsSessionInvalid,
  setIsSessionPaid,
  updatePaymentMethodDictionary,
  removePaymentMethods,
  restorePaymentMethodsFromSession,
  handleIsShippingAddressDisplay,
  resetErrorMsg,
  updateBrowserCountry,
  setShowCancelBtn,
  setTokenType
} = paymentSessionDetailsSlice.actions;

export default paymentSessionDetailsSlice.reducer;


